import { Contact } from '~api/address_book';

import { Button, ButtonLink, Card, ListLayout } from '@ingrid/luterkort-react';

import { ClearConfirmationModal } from '~src/components/ClearConfirmationModal/ClearConfirmationModal';
import { useTranslation } from '~src/i18n/use-translation';
import { useShippingMethodListSuspenseQuery } from '~src/main/queries';
import { createContactFromAddress } from '~src/utils/create-contact-from-address';
import { today } from '~src/utils/internationalized-date';

import { useShipmentHydrateMutation } from '../mutations';
import { useShipmentFormContext } from '../types';
import { useAddresses } from './addresses-context';
import { TemplateSelect, TemplateSelectProps } from './template-select';

export function Template() {
  const { data: methods } = useShippingMethodListSuspenseQuery({ customBookingMethods: true });
  const { watch, setValue, getValues, reset } = useShipmentFormContext();
  const { setContacts } = useAddresses();
  const { t } = useTranslation();

  const hydrateMutation = useShipmentHydrateMutation();
  const template = watch('template');

  const selectProps: Omit<TemplateSelectProps, 'trigger'> = {
    value: template,
    onValueChange: async templateWithTags => {
      const template = templateWithTags?.template?.data;

      if (!template) {
        return;
      }

      const contact_from = createContactFromAddress('template', 'contact_from', template.address_from);
      const contact_to = createContactFromAddress('template', 'contact_to', template.address_to);
      const contact_return = createContactFromAddress('template', 'contact_return', template.address_return);
      const contact_customer_info = createContactFromAddress(
        'template',
        'contact_customer_info',
        template.customer_info?.address,
        template.customer_info?.phone,
        template.customer_info?.email
      );

      const response = await hydrateMutation.mutateAsync({
        ...getValues(),
        ...template,
        shipping_method: methods.find(s => s.id === template.shipping_method),
        customer_info: { contact: contact_customer_info },
        shipping_date: today('UTC').toString(),
        // We need an empty default value for addons, because the template might contain another carrier.
        // Since addons are carrier specific, this means that you could otherwise end up with an addon
        // that is not supported for the carrier that the template refers to. Which would cause a validation error.
        addons: template.addons ?? [],
        contact_return,
        contact_from,
        contact_to,
      });

      reset({
        template: templateWithTags,
        ...response,
      });

      setContacts([contact_from, contact_to, contact_return, contact_customer_info].filter(Boolean) as Contact[]);
    },
  };

  return (
    <Card.Wrapper as="section">
      <Card.Header>{t('common.template')}</Card.Header>
      <Card.Content>
        {template ? (
          <ListLayout.Row>
            <ListLayout.PrimaryText>{template?.template?.data?.name}</ListLayout.PrimaryText>

            <ListLayout.Actions>
              <ListLayout.Row>
                <TemplateSelect {...selectProps} trigger={<ButtonLink>{t('common.edit')}</ButtonLink>} />
              </ListLayout.Row>

              <ListLayout.Row>
                <ClearConfirmationModal
                  clearedItemName="common.template"
                  onSubmit={() => setValue('template', undefined)}
                />
              </ListLayout.Row>
            </ListLayout.Actions>
          </ListLayout.Row>
        ) : (
          <TemplateSelect
            {...selectProps}
            trigger={<Button variant="secondary">{t('shipments.details.selectTemplate')}</Button>}
          />
        )}
      </Card.Content>
    </Card.Wrapper>
  );
}
